import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import ArticleCard from "@/components/feature/ArticleCard";
import useSeo from "@/hooks/useSeo";
import { categories } from "@/mocks/categories";
import { articles, heroFeatured, getArticleBySlug } from "@/mocks/articles";
import { SITE_NAME, SITE_SLOGAN, SITE_DESC } from "@/config/site";
import { organizationSchema, websiteSchema, webPageSchema } from "@/lib/schema";

const featured = heroFeatured.map((s) => getArticleBySlug(s)).filter(Boolean) as typeof articles;
const recent = articles.slice(0, 8);

export default function Home() {
  useSeo({
    title: "日漫神作精选 · 日漫恋爱热血悬疑治愈机战推荐大全",
    description: SITE_DESC,
    path: "/",
    jsonLd: [organizationSchema(), websiteSchema(), webPageSchema("日漫精选推荐", "/", SITE_DESC)],
  });

  return (
    <div className="flex min-h-screen flex-col">
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=cinematic%20stylized%20anime%20landscape%20of%20a%20sprawling%20city%20skyline%20at%20dusk%20with%20dramatic%20vermillion%20orange%20and%20gold%20sky%2C%20floating%20clouds%20and%20distant%20mountains%2C%20birds%20silhouetted%20in%20the%20distance%2C%20warm%20dramatic%20lighting%2C%20film%20grain%2C%20high%20detail%20digital%20art&width=1920&height=860&seq=hero-riman-home&orientation=landscape"
              alt="日漫精选推荐的夕阳城市景观背景"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
          </div>

          <div className="relative mx-auto flex h-[420px] w-full flex-col items-center justify-center px-4 text-center md:h-[600px]">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-accent-500/90 px-4 py-1.5 text-xs font-bold text-foreground-950">
              <i className="ri-fire-fill" aria-hidden="true" />
              日漫神作精选榜单 · 持续更新
            </span>
            <h1 className="font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
              告别漫荒，一次看尽{` `}
              <span className="text-accent-400">{SITE_NAME}</span>
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-background-100 md:text-base">
              {SITE_SLOGAN}。日漫恋爱、日漫热血、日漫悬疑、日漫治愈、日漫机战神作一站推荐。
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a
                href="#featured"
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                查看神作精选
              </a>
              <a
                href="#categories"
                className="whitespace-nowrap rounded-md bg-background-50/15 px-6 py-3 text-sm font-semibold text-background-50 ring-1 ring-background-50/40 transition-colors hover:bg-background-50/25"
              >
                浏览全部题材
              </a>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section id="categories" className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="mb-8 flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary-700">题材分类</p>
              <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                按口味挑选你的日漫
              </h2>
            </div>
            <Link
              to="/category/rexue"
              className="whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
            >
              查看全部题材 <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="group relative overflow-hidden rounded-lg border border-background-200"
              >
                <div className="relative h-44 w-full overflow-hidden md:h-48">
                  <img
                    src={c.banner}
                    alt={`${c.name}分类`}
                    title={`${c.name} - 日漫精选推荐`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="font-heading text-lg font-bold text-background-50">{c.name}</h3>
                    <p className="mt-1 line-clamp-2 text-xs text-background-100/90">{c.desc}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured ranking */}
        <section id="featured" className="bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <div className="mb-8 flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-end">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-accent-700">巅峰之选</p>
                <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  日漫神作精选榜单
                </h2>
              </div>
              <Link
                to="/category/shenzuo"
                className="whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
              >
                进入神作专题 <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>

            <ol className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {featured.map((article, idx) => (
                <li key={article.slug} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex h-12 w-10 shrink-0 flex-col items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <span className="font-heading text-xl font-black leading-none">{idx + 1}</span>
                    <span className="text-[10px]">TOP</span>
                  </span>
                  <Link to={`/article/${article.slug}`} className="group flex flex-1 gap-4">
                    <div className="h-24 w-16 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={article.cover}
                        alt={`${article.title}封面`}
                        title={article.title}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="flex flex-col justify-between py-0.5">
                      <div>
                        <h3 className="line-clamp-2 font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-700">
                          {article.title}
                        </h3>
                        <p className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                          <span className="flex items-center gap-1 text-accent-700">
                            <i className="ri-star-fill" aria-hidden="true" />
                            {article.rating.toFixed(1)}
                          </span>
                          <span>{article.categoryName}</span>
                        </p>
                      </div>
                      <span className="text-xs font-medium text-primary-700">
                        查看推荐 <i className="ri-arrow-right-line" aria-hidden="true" />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Recent recommendations */}
        <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="mb-8 flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-secondary-700">最新推荐</p>
              <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                热门日漫神作速览
              </h2>
            </div>
            <Link
              to="/category/shenzuo"
              className="whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
            >
              查看更多 <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
            {recent.map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mx-auto w-full max-w-7xl px-4 pb-14 md:px-6 md:pb-20">
          <div className="relative overflow-hidden rounded-lg">
            <img
              src="https://readdy.ai/api/search-image?query=stylized%20anime%20illustration%20of%20scattered%20movie%20reels%20and%20film%20strips%20with%20warm%20golden%20light%20bursts%2C%20cinematic%20atmosphere%2C%20rich%20vermillion%20and%20amber%20tones%2C%20high%20quality%20digital%20art&width=1600&height=460&seq=cta-riman&orientation=landscape"
              alt="日漫神作精选横幅背景"
              className="h-56 w-full object-cover md:h-72"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
            <div className="absolute inset-0 flex flex-col items-start justify-center px-6 md:px-12">
              <h2 className="font-heading text-2xl font-black text-background-50 md:text-4xl">
                还在为选番发愁？
              </h2>
              <p className="mt-2 max-w-lg text-sm text-background-100/90 md:text-base">
                无论你想看甜甜的恋爱、燃爆的热血，还是烧脑的悬疑、治愈的日常，都能在这里找到神作。
              </p>
              <Link
                to="/category/lianai"
                className="mt-5 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-600"
              >
                开始挑选你的日漫
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}